// adoptionAddendumForm — the pure model behind /send/adoption-addendum (WEL-131).
//
// Kept free of `./api` (and its Vite-only `import.meta.env`) so every rule here
// is unit-testable under node:test/tsx, and so the page stays presentation.
//
// THE SHAPE OF THIS DOCUMENT, and why it isn't the leaseholder change:
//   • Nobody leaves. There is no Continuing/Outgoing decision to make, no
//     effective date, no release. Everyone already on the lease STAYS — and
//     signs, because they are consenting to the addition.
//   • So the only real inputs are: who is joining, and the two lease dates the
//     addendum's prose quotes back ("the Lease Agreement dated …", "will expire
//     on …"). Both are required: a blank renders a legal document that reads
//     "dated ." — worse than refusing to send.
//   • Minors on the application are shown but EXCLUDED — they join the household
//     as occupants, not leaseholders.

import { partitionCandidatePeople } from './leaseholderChangeMatch';
import { applicantLikelyAdult } from './applicantAdult';
import type {
  AdoptionAddendumCandidate,
  AdoptionAddendumPerson,
  SendAdoptionAddendumBody,
} from './adoptionAddendumClient';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A resident already on the lease. They consent to the addition and sign. */
export interface OnLeaseRow {
  key: number;
  /** AppFolio tenant id, when this row came from the tenancy. */
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  /**
   * This person is ALSO on the incoming application — AppFolio pre-created them
   * as a Future-status tenant, so they surface on both lists. They are counted
   * ONCE, here, and flagged so the UI can say why they aren't under "joining".
   */
  alsoOnApplication: boolean;
}

/** A resident being ADDED. `source: 'application'` rows come from the picked
 *  application (identity fixed, email editable); 'manual' rows are typed by hand. */
export interface JoiningRow {
  key: number;
  source: 'application' | 'manual';
  firstName: string;
  lastName: string;
  email: string;
  /** A KNOWN minor: shown for context, never sent as a signer. */
  minor: boolean;
}

export interface AdoptionAddendumFormState {
  onLease: OnLeaseRow[];
  joining: JoiningRow[];
  premises: string;
  /** yyyy-mm-dd — the sitting lease's start, quoted in the addendum's prose. */
  leaseStartDate: string;
  /** yyyy-mm-dd — the sitting lease's end, quoted in the addendum's prose. */
  leaseEndDate: string;
  otherTerms: string;
}

let nextKey = 1;
/** Reset the row-key counter. Tests only — keys must be stable within a session. */
export function __resetKeysForTest(): void {
  nextKey = 1;
}

export function blankJoiningRow(): JoiningRow {
  return { key: nextKey++, source: 'manual', firstName: '', lastName: '', email: '', minor: false };
}

export function fullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

/**
 * Seed the whole form from a picked candidate.
 *
 * The dedup is load-bearing: an approved applicant AppFolio has already
 * pre-created as a Future tenant appears in BOTH `currentResidents` and
 * `applicants`. Listing them twice would put the same person on the document
 * twice — two signature blocks, two invites. `partitionCandidatePeople` keeps
 * them once, under "on the lease today", and drops them from the joiners.
 */
export function seedFromCandidate(
  candidate: AdoptionAddendumCandidate,
  today: Date = new Date(),
): AdoptionAddendumFormState {
  const { residents, applicants } = partitionCandidatePeople(
    candidate.currentResidents,
    candidate.applicants,
  );
  return {
    onLease: residents.map((r) => ({
      key: nextKey++,
      tenantId: r.tenantId,
      firstName: r.firstName,
      lastName: r.lastName,
      email: (r.email ?? '').trim(),
      alsoOnApplication: r.incoming,
    })),
    joining: applicants.map((a) => ({
      key: nextKey++,
      source: 'application' as const,
      firstName: a.firstName,
      lastName: a.lastName,
      email: (a.email ?? '').trim(),
      minor: !applicantLikelyAdult(a, today),
    })),
    premises: candidate.propertyAddress,
    leaseStartDate: candidate.leaseStartDate,
    leaseEndDate: candidate.leaseEndDate,
    otherTerms: '',
  };
}

/** The joiners that will actually be sent — named adults, minors excluded. */
export function includedJoining(rows: JoiningRow[]): JoiningRow[] {
  return rows.filter((r) => !r.minor && !!fullName(r.firstName, r.lastName));
}

/** True when a row's email is present but malformed (drives the field's error state). */
export function isEmailInvalid(email: string): boolean {
  const e = email.trim();
  return !!e && !EMAIL_RE.test(e);
}

/** A signer row is ready when it has a name AND a valid email. */
export function isSignerRowValid(row: { firstName: string; lastName: string; email: string }): boolean {
  return !!fullName(row.firstName, row.lastName) && EMAIL_RE.test(row.email.trim());
}

/**
 * Everything wrong with the form right now, in the order a person would fix it.
 * Empty array = ready to send. Each string is shown verbatim, so they read as
 * instructions rather than error codes.
 */
export function validateAdoptionAddendumForm(state: AdoptionAddendumFormState): string[] {
  const problems: string[] = [];
  const joiners = includedJoining(state.joining);

  if (state.onLease.length === 0) {
    problems.push(
      'No one is on the lease today — an addendum amends an existing lease. Check that this unit has a current tenancy.',
    );
  }
  if (joiners.length === 0) {
    problems.push('Add at least one resident who is joining the lease.');
  }

  // A half-filled manual row (an email typed, no name yet) is dropped by
  // includedJoining's name filter — so without this it would silently not be sent,
  // never get email-validated, and leave the panel saying "add at least one
  // resident" while a visibly filled row sits on screen. Say what's missing.
  for (const r of state.joining) {
    if (r.minor || fullName(r.firstName, r.lastName)) continue;
    if (!r.email.trim()) continue; // an untouched blank row is inert, not an error
    problems.push('The person you are adding needs a first and last name — or remove the row.');
  }

  const missingEmail = [...state.onLease, ...joiners].filter(
    (r) => !r.email.trim() || isEmailInvalid(r.email),
  );
  for (const r of missingEmail) {
    const name = fullName(r.firstName, r.lastName) || 'One resident';
    problems.push(`${name} needs a valid email address — everyone named on the addendum signs it.`);
  }

  // Two signers sharing an address collide in DocuSeal (one slot swallows the
  // other's signature), so it is refused here rather than half-sent.
  const seen = new Map<string, string>();
  for (const r of [...state.onLease, ...joiners]) {
    const email = r.email.trim().toLowerCase();
    if (!email) continue;
    const priorName = seen.get(email);
    if (priorName) {
      problems.push(
        `${fullName(r.firstName, r.lastName) || 'A resident'} and ${priorName} share the email ${email} — each signer needs their own.`,
      );
    } else {
      seen.set(email, fullName(r.firstName, r.lastName) || 'another resident');
    }
  }

  if (!state.premises.trim()) {
    problems.push('Enter the premises address — the addendum names it.');
  }
  if (!isYmd(state.leaseStartDate)) {
    problems.push('Enter the date of the lease this addendum amends.');
  }
  if (!isYmd(state.leaseEndDate)) {
    problems.push('Enter the date the lease term ends.');
  }
  if (
    isYmd(state.leaseStartDate) &&
    isYmd(state.leaseEndDate) &&
    state.leaseEndDate <= state.leaseStartDate
  ) {
    problems.push('The lease term has to end after it starts — check both dates.');
  }

  return problems;
}

export function canSendAdoptionAddendum(state: AdoptionAddendumFormState): boolean {
  return validateAdoptionAddendumForm(state).length === 0;
}

/** yyyy-mm-dd that is also a real calendar date (2026-02-31 is not). */
export function isYmd(value: string): boolean {
  const s = (value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === s;
}

const toPerson = (r: {
  firstName: string;
  lastName: string;
  email: string;
}): AdoptionAddendumPerson => ({
  firstName: r.firstName.trim(),
  lastName: r.lastName.trim(),
  email: r.email.trim(),
});

/**
 * The send body. Only VALIDATED state should reach this — call
 * canSendAdoptionAddendum first. `otherTerms` is omitted when blank so the API
 * renders nothing rather than a dangling legal lead-in.
 */
export function buildAdoptionAddendumBody(
  state: AdoptionAddendumFormState,
): SendAdoptionAddendumBody {
  const terms = state.otherTerms.trim();
  return {
    currentResidents: state.onLease.map(toPerson),
    adoptees: includedJoining(state.joining).map(toPerson),
    premises: state.premises.trim(),
    startDate: state.leaseStartDate,
    endDate: state.leaseEndDate,
    ...(terms ? { otherTerms: terms } : {}),
  };
}

/** "Ann Lee, Bob Lee and Newt Joiner" — for the confirmation summary line. */
export function joinNames(rows: Array<{ firstName: string; lastName: string }>): string {
  const names = rows.map((r) => fullName(r.firstName, r.lastName)).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** How many people will be emailed a signing link right away (the resident parties). */
export function signerCount(state: AdoptionAddendumFormState): number {
  return state.onLease.length + includedJoining(state.joining).length;
}
